import { Button, Css, Palette } from "@homebound/beam";
import AwsS3 from "@uppy/aws-s3";
import Compressor from "@uppy/compressor";
import type { Body, Meta, UppyFile } from "@uppy/core";
import Uppy from "@uppy/core";
import "@uppy/core/css/style.css";
import "@uppy/dashboard/css/style.css";
import ImageEditor from "@uppy/image-editor";
import "@uppy/image-editor/css/style.css";
import Dashboard from "@uppy/react/dashboard";
import { useCallback, useMemo, useRef, useState } from "react";
import { Icon } from "src/components/Icon";
import { useCreateAssetUploadUrlMutation } from "src/components/uploader/UppyUploader.generated";
import { useUploaderContext } from "src/contexts/UploaderContext";
import { AssetResizing } from "src/generated/graphqlTypes";

/** The `createAssetUploadUrl` metadata for a given file. */
export type AssetMetadata = Meta & {
  s3Key: string;
  /** The URL that Uppy uploads to, generally not for external use. */
  signedUrl: string;
  /** _After_ the upload is complete, the download url that could be used to download the S3 key. */
  downloadUrl: string;
  /** _After_ the upload is complete, the preview url that could be used to download the S3 key. */
  previewUrl: string;
};

/** Convenience alias so consumers don't need to import `Body` from `@uppy/core`. */
export type AssetUppyFile = UppyFile<AssetMetadata, Body>;

export type UseUppyUploaderProps = {
  /** Note: When only "image/svg+xml" is allowed, the file will NOT be compressed. This is because we need to be able to
   * manipulate the SVG as needed (IE: Floor plans) later
   */
  allowedFileTypes?: Array<string>;
  /** This will emit once per uploaded file */
  onFinish: (file: AssetUppyFile) => void;
  onError?: (file: AssetUppyFile) => void;
  /** This will emit once when all files were uploaded */
  onComplete?: () => void;
  maxNumberOfFiles?: number;
  /** passed to Uppy, start uploading as soon as files are selected? */
  autoProceed?: boolean;
  /** request thumbnail previews for uploaded files */
  previewResizing?: AssetResizing;
};

/**
 * Allows using uppy headlessly, i.e. with the page's own `input type=file` elements/UX.
 *
 * This has the same return type as the `useUppy` hook, but configured to call `createAssetUploadUrl` and
 * then upload files to the resulting S3 keys.
 */
export function useUppyUploader(props: UseUppyUploaderProps): Uppy<AssetMetadata, Body> {
  const { allowedFileTypes, onFinish, onComplete, onError, previewResizing, maxNumberOfFiles = null } = props;

  const onFinishRef = useRef<Function>();
  onFinishRef.current = onFinish;
  const onCompleteRef = useRef<Function>();
  onCompleteRef.current = onComplete;
  const onErrorRef = useRef<Function>();
  onErrorRef.current = onError;

  const { setFiles, clearFiles } = useUploaderContext();
  const [createAssetUploadUrl] = useCreateAssetUploadUrlMutation();

  const skipCompression = allowedFileTypes?.length === 1 && allowedFileTypes[0] === "image/svg+xml";

  return useMemo(() => {
    const uppy = new Uppy<AssetMetadata, Body>({
      autoProceed: props.autoProceed ?? true,
      restrictions: { maxNumberOfFiles, minNumberOfFiles: 1, allowedFileTypes },
    })
      .use(ImageEditor, {
        quality: 1,
        cropperOptions: { croppedCanvasOptions: {}, viewMode: 1, background: false, autoCropArea: 1, responsive: true },
        actions: {
          granularRotate: true,
          revert: true,
          rotate: true,
          flip: false,
          zoomIn: false,
          zoomOut: false,
          cropSquare: true,
          cropWidescreen: true,
          cropWidescreenVertical: false,
        },
      })
      .use(AwsS3, {
        shouldUseMultipart: false,
        getUploadParameters: async (file) => {
          const { data } = await createAssetUploadUrl({
            variables: { input: { contentType: file.type!, previewResizing } },
          });
          const { s3Key, uploadUrl: signedUrl, downloadUrl, previewUrl } = data?.createAssetUploadUrl!;
          file.meta = { ...file.meta, s3Key, signedUrl, downloadUrl, previewUrl };
          return { method: "PUT" as const, url: signedUrl, headers: { "Content-Type": file.type! } };
        },
      })
      .on("upload-progress", (file, progress) => {
        if (!file || !progress.bytesTotal) return;
        const progressPercentage = Math.round((progress.bytesUploaded * 100) / progress.bytesTotal);
        setFiles({ [file.name]: { name: file.name, progress: progressPercentage } });
      })
      .on("upload-success", (file) => {
        if (file) onFinishRef.current?.(file);
      })
      .on("upload-error", (file, error) => {
        if (!file) return;
        console.error(`error with file: ${file.name}, error message: ${error}`);
        onErrorRef.current?.(file);
      });

    if (!skipCompression) {
      uppy.use(Compressor, { quality: 0.9 }); // This ensures correct rotation
    }

    uppy.on("complete", () => {
      onCompleteRef.current?.();
      // Once all files completed uploading (and total progress is 100 that means there is not pending retries) call clear
      // to return things to the way they were before any user input.
      // This to clear file list and allow user to upload same file multiple times
      if (uppy.getState().totalProgress === 100) {
        // Clear and close uploader after 5 secs of completing
        setTimeout(() => clearFiles(), 5000);
        uppy.clear();
      }
    });
    return uppy;
    // TODO: validate this eslint-disable. It was automatically ignored when the oxlint migration restored `react-hooks/exhaustive-deps` to an error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

export type UppyUploaderProps = Omit<UseUppyUploaderProps, "autoProceed"> & {
  /**
   * Dashboard is a larger component that allows image previews and shows upload progress
   * DragDrop renders a simple drag and drop area for file selection
   */
  type?: "Dashboard" | "DragDrop" | "FileInput";
  /** Text that will show in the FileInput **/
  fileInputText?: string;
  /** Text that will show in the drop zone */
  dragDropText?: string;
  dragDropWidth?: number | string;
  dragDropHeight?: number | string;
  /** Error message to display inside the drop zone */
  errorMessage?: string;
};

export function UppyUploader(props: UppyUploaderProps) {
  const {
    dragDropText = "Drag & drop files",
    fileInputText = "Choose Files",
    allowedFileTypes,
    dragDropWidth = 250,
    dragDropHeight = 40,
    type = "DragDrop",
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const uppy = useUppyUploader({ ...props, autoProceed: type === "DragDrop" || type === "FileInput" });

  if (type === "Dashboard") {
    return <Dashboard uppy={uppy as any} plugins={["ImageEditor"]} height={400} />;
  }

  if (type === "FileInput") {
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const [file] = event.target.files || [];
      if (file) {
        uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
      }
    };
    return (
      <div>
        <input
          id="uppyFileInput"
          css={Css.dn.$}
          ref={inputRef}
          type="file"
          accept={allowedFileTypes?.join()}
          onChange={handleFileChange}
        />
        <Button label={fileInputText} onClick={() => inputRef.current!.click()} />
      </div>
    );
  }

  // This is `type === "DragDrop"`
  const parentWidth = typeof dragDropWidth === "number" ? `${dragDropWidth + 2}px` : dragDropWidth;
  const parentHeight = typeof dragDropHeight === "number" ? `${dragDropHeight + 2}px` : dragDropHeight;
  return (
    <CustomDropZone
      uppy={uppy}
      allowedFileTypes={allowedFileTypes}
      dragDropText={dragDropText}
      errorMessage={props.errorMessage}
      width={parentWidth}
      height={parentHeight}
    />
  );
}

/** Custom drop zone that replaces Uppy's DragDrop, making the entire area a drag target and click target. */
export function CustomDropZone<M extends Meta>(props: {
  uppy: Uppy<M, Body>;
  allowedFileTypes?: string[];
  dragDropText: string;
  errorMessage?: string;
  width: string;
  height: string;
}) {
  const { uppy, allowedFileTypes, dragDropText, errorMessage, width, height } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Derive upload progress from UploaderContext (already populated by useUppyUploader's upload-progress handler)
  const { files } = useUploaderContext();
  const fileEntries = Object.values(files);
  const isUploading = fileEntries.length > 0 && fileEntries.some((f) => f.progress < 100);
  const totalProgress = isUploading
    ? Math.round(fileEntries.reduce((sum, f) => sum + f.progress, 0) / fileEntries.length)
    : undefined;

  const addFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList) return;
      Array.from(fileList).forEach((file) => {
        try {
          uppy.addFile({ name: file.name, size: file.size, type: file.type, data: file });
        } catch (err) {
          // Uppy throws if file doesn't match restrictions; ignore duplicates
          console.warn(err);
        }
      });
    },
    [uppy],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDraggingOver(false);
      addFiles(e.dataTransfer.files);
    },
    [addFiles],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  }, []);

  return (
    <div
      css={
        Css.df.fdc.aic.jcc.ba.bcBlue700.bsDashed.br8.cursorPointer.w(width).h(height).bgBlue50.if(isDraggingOver)
          .bgBlue100.$
      }
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() => inputRef.current?.click()}
      data-testid="uppy-drop-zone"
    >
      <input
        ref={inputRef}
        type="file"
        // Preserve class name for test utility compatibility (getUppyDragDropInput)
        className="uppy-DragDrop-input"
        css={Css.dn.$}
        accept={allowedFileTypes?.join(",")}
        multiple
        onChange={(e) => {
          addFiles(e.target.files);
          // Reset so the same file can be re-selected
          e.target.value = "";
        }}
      />
      {totalProgress !== undefined ? (
        <UploadProgressBar progress={totalProgress} />
      ) : (
        <>
          <div css={Css.df.aic.if(!!dragDropText).gap1.$}>
            <Icon icon="cloudUpload" color={Palette.Blue700} inc={3} />
            {dragDropText && <span css={Css.blue700.$}>{dragDropText}</span>}
          </div>
          {errorMessage && (
            <div data-testid="error-message" css={Css.xs2Sb.red700.mt1.$}>
              {errorMessage}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function UploadProgressBar(props: { progress: number }) {
  const { progress } = props;
  return (
    <div css={Css.w75.df.fdc.aic.gap1.$}>
      <span css={Css.sm.blue700.$}>Uploading... {progress}%</span>
      <div css={Css.w100.hPx(6).bgGray300.br4.oa.$}>
        <div css={Css.hPx(6).bgBlue700.br4.add("transition", "width 150ms ease").add("width", `${progress}%`).$} />
      </div>
    </div>
  );
}
